import type { ImportedTopologyFaceTriangleRangeBinding } from './types'
import { asRecord } from './utils'

/**
 * 判断输入是否为可用的三角面半开区间。
 *
 * @param value 原始区间值。
 * @returns 是否为合法区间。
 */
function isValidTriangleRangeTuple(value: unknown): value is [number, number] {
  return Array.isArray(value)
    && value.length >= 2
    && Number.isInteger(value[0])
    && Number.isInteger(value[1])
    && value[0] >= 0
    && value[1] > value[0]
}

/**
 * 读取后端可能输出的三角面区间字段。
 *
 * @param value 原始区间承载对象。
 * @returns 归一化后的三角面区间。
 */
export function readImportedTriangleRangeTuple(value: unknown): [number, number] | null {
  if (isValidTriangleRangeTuple(value)) {
    return [value[0], value[1]]
  }

  const record = asRecord(value)
  if (isValidTriangleRangeTuple(record?.triangleRange)) {
    return [record.triangleRange[0], record.triangleRange[1]]
  }

  if (isValidTriangleRangeTuple(record?.value)) {
    return [record.value[0], record.value[1]]
  }

  return null
}

/**
 * 归一化可选字符串字段。
 *
 * @param value 原始字段值。
 * @returns 非空字符串；无效时返回 `undefined`。
 */
function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 归一化可选整数字段。
 *
 * @param value 原始字段值。
 * @returns 非负整数；无效时返回 `undefined`。
 */
function readOptionalNonNegativeInteger(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined
}

/**
 * 读取单条拓扑面到 GLB 可见三角区间的显式绑定。
 *
 * @param value 原始绑定记录。
 * @param bodyId 当前 body ID；后端提供 bodyId 时用于过滤其它 body 的范围。
 * @returns 归一化绑定；无效或不属于当前 body 时返回 `null`。
 */
function readImportedTopologyFaceTriangleRangeBinding(
  value: unknown,
  bodyId?: string,
): ImportedTopologyFaceTriangleRangeBinding[] {
  const record = asRecord(value)
  if (!record) {
    return []
  }

  const rangeBodyId = readOptionalString(record.bodyId)
  if (bodyId && rangeBodyId && rangeBodyId !== bodyId) {
    return []
  }

  const singleRange = readImportedTriangleRangeTuple(record.triangleRange ?? record.value)
  const multipleRanges = Array.isArray(record.triangleRanges)
    ? record.triangleRanges
      .map(readImportedTriangleRangeTuple)
      .filter((range): range is [number, number] => Boolean(range))
    : []
  const triangleRanges = singleRange ? [singleRange] : multipleRanges
  if (triangleRanges.length === 0) {
    return []
  }

  const faceIndex = readOptionalNonNegativeInteger(record.faceIndex)
  const faceRef = readOptionalString(record.faceRef)
  const subEntityId = readOptionalString(record.subEntityId)
  if (faceIndex === undefined && !faceRef && !subEntityId) {
    return []
  }

  return triangleRanges.map((triangleRange) => ({
    faceIndex,
    faceRef,
    subEntityId,
    triangleRange,
  }))
}

/**
 * 读取拓扑面到 GLB 可见三角区间的显式绑定集合。
 *
 * 设计说明：
 * 1. GLB primitive extras 与 topo sidecar 都可能承载同一类 face range 协议；
 * 2. 这里统一归一化，避免 loader、sidecar parser 和 FeatureFace provider 各自打散补丁；
 * 3. 返回值只包含纯数据，不持有任何 three.js 对象，可安全缓存在导入资产中。
 *
 * @param value 原始绑定集合。
 * @param bodyId 当前 body ID；存在时过滤其它 body 的绑定。
 * @returns 归一化绑定集合。
 */
export function readImportedTopologyFaceTriangleRangeBindings(
  value: unknown,
  bodyId?: string,
): ImportedTopologyFaceTriangleRangeBinding[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .flatMap((item) => readImportedTopologyFaceTriangleRangeBinding(item, bodyId))
}
